import React, { useState, useMemo } from "react";
import BreadCrumbs from "../../../components/BreadCrumbs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Card, CardBody, CardHeader } from "react-bootstrap";
import { faPlus, faSpinner, faTrash } from "@fortawesome/free-solid-svg-icons";
import {
  PortionDetails,
  useCreatePortionMutation,
  useDeletePortionMutation,
} from "../../../redux/services/CustomTypeService";
import DataTable, { TableColumn } from "react-data-table-component";
import ButtonComponent from "../../../components/ButtonComponent";
import ControlledRowTextField from "../../../components/ControlledRowTextField";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { Col, Row } from "reactstrap";
import TableLoadingComponent from "../../../components/TableLoadingComponent";
import Swal from "sweetalert2";
import { useRestaurantDetails } from "../../../hooks/useFirebaseOrders";
import { useFirebaseMenuItemsList, useFirebaseSetMenuList, useFirebaseCustomMenuList } from "../../../hooks/useFirebaseMenu";

const PortionSizes = () => {

  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  const [deletingPortionSize, setDeletingPortionSize] = useState<string | null>(
    null
  );

  const [createPortion, { isLoading: isCreationLoading }] =
    useCreatePortionMutation();
  const { data: restaurantData, loading: restaurantLoading } =
    useRestaurantDetails(restaurantId);
  const { data: menuItemsData, loading: menuItemsLoading } = 
    useFirebaseMenuItemsList(restaurantId);
  const { data: setMenusData, loading: setMenusLoading } = 
    useFirebaseSetMenuList(restaurantId);
  const { data: customMenusData, loading: customMenusLoading } = 
    useFirebaseCustomMenuList(restaurantId);
  const [deletePortion] = useDeletePortionMutation();

  const loading = restaurantLoading || menuItemsLoading || setMenusLoading || customMenusLoading;

  // Calculate portion sizes with item counts
  const portionSizes = useMemo(() => {
    if (!restaurantData?.portionSizes) return [];
    
    const menuItems = menuItemsData?.menuItems || [];
    const setMenus = setMenusData?.setMenus || [];
    const customMenus = customMenusData?.customMenus || [];
    
    return restaurantData.portionSizes.map((portionSize: string) => {
      // Count from menuItems
      const menuItemsCount = menuItems.filter((item: any) => 
        item.portions?.some((p: any) => p.displayName === portionSize)
      ).length;
      
      // Count from setMenus
      const setMenusCount = setMenus.filter((item: any) => 
        item.portions?.some((p: any) => p.displayName === portionSize)
      ).length;
      
      // Count from customMenus
      const customMenusCount = customMenus.filter((item: any) => 
        item.portions?.some((p: any) => p.displayName === portionSize)
      ).length;
      
      const itemCount = menuItemsCount + setMenusCount + customMenusCount;
      
      return {
        portionSize,
        itemCount
      };
    });
  }, [restaurantData, menuItemsData, setMenusData, customMenusData]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm();

  // Watch the input value to enable/disable the Add button
  const displayNameWatcher = watch("displayName");

  const onSubmit = async (data: any) => {
    try {
      await createPortion({
        restaurantId: restaurantId,
        portionSize: data.displayName,
      }).unwrap();

      reset();
      toast.success("Portion size created");
      // Firebase will automatically update the data via real-time listener
    } catch (error: any) {
      if (error.status === 409) {
        toast.error(error.data);
        return;
      }
      console.error("Error creating portion size:", error);
      toast.error("Something went wrong");
    }
  };

  // const handleDeleteClick = async (portionSize: string) => {
  //   setDeletingPortionSize(portionSize);
  //   try {
  //     await deletePortion({
  //       restaurantId,
  //       portionSize: portionSize,
  //     }).unwrap();
  //     toast.success("Portion size deleted");

  //     setPortionSizes(
  //       portionSizes.filter((type) => type.portionSize !== portionSize)
  //     );
  //   } catch (error: any) {
  //     console.error("Delete Error:", error);
  //     toast.error(error.data || "Error deleting portion size");
  //   } finally {
  //     setDeletingPortionSize(null);
  //   }
  // };
  const handleDeleteClick = (portionSize: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setDeletingPortionSize(portionSize);
        try {
          await deletePortion({
            restaurantId,
            portionSize: portionSize,
          }).unwrap();
          toast.success("Portion size deleted");
          // Firebase will automatically update the data via real-time listener
        } catch (error: any) {
          console.error("Delete Error:", error);
          toast.error(error.data || "Error deleting portion size");
        } finally {
          setDeletingPortionSize(null);
        }
      }
    });
  };

  const columns: TableColumn<PortionDetails>[] = [
    {
      name: <h6 className="fw-bold">Portion Size</h6>,
      selector: (row) => row.portionSize,
    },
    {
      name: <h6 className="fw-bold">Available Count</h6>,
      selector: (row) => row.itemCount,
    },
    {
      name: <h6 className="fw-bold">Actions</h6>,
      cell: (row) => (
        <div className="menuitem-action-buttons">
          {deletingPortionSize === row.portionSize ? (
            <Button variant="light" disabled={true}>
              <FontAwesomeIcon icon={faSpinner} spin />
            </Button>
          ) : (
            <Button
              variant="light"
              onClick={() => handleDeleteClick(row.portionSize)}
            >
              <FontAwesomeIcon
                icon={faTrash}
                title="Delete"
                className="menuitem-delete-button"
              />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <Card className="mt-3">
        <CardHeader className="header-bg-color">
          <h5>Portion Sizes</h5>
        </CardHeader>
        <CardBody>
          <form
            className="customize-type-form mt-2"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row align-items-end">
              <Row>
                <Col lg="10">
                  <ControlledRowTextField
                    label={"Portion Size"}
                    inputName={"displayName"}
                    inputType={"text"}
                    placeholder={"Enter Portion Size"}
                    control={control}
                    rules={{
                      required: "Portion size is required",
                      pattern: {
                        value: /^[A-Z0-9_]+$/,
                        message:
                          "Only capital letters, numbers, and underscores are allowed",
                      },
                    }}
                  />
                  {errors.displayName && (
                    <span className="text-warning small">
                      {errors.displayName.message as string}
                    </span>
                  )}
                </Col>
                <Col lg="2">
                  {isCreationLoading ? (
                    <ButtonComponent
                      type={"submit"}
                      icon={<FontAwesomeIcon icon={faSpinner} spin />}
                      titel="Adding..."
                      isDisabled={true}
                    />
                  ) : (
                    <ButtonComponent
                      type="submit"
                      icon={
                        <FontAwesomeIcon
                          icon={faPlus}
                          title={"Add Portion Size"}
                        />
                      }
                      titel="Add"
                      isDisabled={!displayNameWatcher}
                    />
                  )}
                </Col>
              </Row>
            </div>
          </form>
        </CardBody>
      </Card>
      <Card className="mt-3">
        <CardBody className="40">
          {loading ? (
            <TableLoadingComponent numColumns={3} />
          ) : (
            <DataTable columns={columns} data={portionSizes} />
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default PortionSizes;
